import { useRef, useEffect, useState } from 'react';
import { useGesture } from '@use-gesture/react';
import { Loader2 } from 'lucide-react';
import { RARITY_UI } from '../../lib/rarity';
import { cardImageUrl } from './ARCardPicker';
import { getCardCutout } from '../../lib/cardCutout';

/**
 * Draggable / pinchable / rotatable card overlay for AR Field-Team mode.
 *
 * mode = 'card'      → framed card portrait (rarity border, watermark, caption)
 * mode = 'creature'  → transparent-background creature cutout with soft drop
 *                      shadow so it composites naturally into the selfie.
 *
 * The parent owns the transform state ({x, y, scale, rotate}) so it can
 * later composite the overlay into the captured frame at the same pose.
 * All coordinates are in CSS pixels relative to the parent (fixed
 * fullscreen container).
 */
export default function ARCardOverlay({ card, transform, setTransform, containerRef, mode = 'creature', onCutoutReady, active = true, onActivate }) {
  const ref = useRef(null);
  const ui = RARITY_UI[card.rarity] || RARITY_UI.common;
  const rawImg = cardImageUrl(card);
  const [cutoutUrl, setCutoutUrl] = useState(null);
  const [cutting, setCutting] = useState(false);
  const [cutErr, setCutErr] = useState(false);

  // Kick off background removal lazily whenever a new card is picked so it's
  // ready by the time the user hits the shutter (usually within 1-3s).
  useEffect(() => {
    if (!rawImg) return;
    let cancelled = false;
    setCutoutUrl(null);
    setCutErr(false);
    setCutting(true);
    getCardCutout(rawImg)
      .then(url => {
        if (cancelled) return;
        setCutoutUrl(url);
        onCutoutReady?.(url);
      })
      .catch(() => { if (!cancelled) setCutErr(true); })
      .finally(() => { if (!cancelled) setCutting(false); });
    return () => { cancelled = true; };
  }, [rawImg, onCutoutReady]);

  // Clamp so the overlay can never fully leave the viewport
  const clamp = (t) => {
    const el = containerRef?.current;
    if (!el) return t;
    const rect = el.getBoundingClientRect();
    const halfW = t.width * t.scale * 0.5;
    const halfH = t.height * t.scale * 0.5;
    const margin = 60;
    return {
      ...t,
      x: Math.min(Math.max(t.x, -rect.width / 2 - halfW + margin), rect.width / 2 + halfW - margin),
      y: Math.min(Math.max(t.y, -rect.height / 2 - halfH + margin), rect.height / 2 + halfH - margin),
    };
  };

  useGesture(
    {
      onDragStart: () => onActivate?.(),
      onPinchStart: () => onActivate?.(),
      onDrag: ({ offset: [ox, oy], pinching, cancel }) => {
        if (pinching) return cancel();
        setTransform(t => clamp({ ...t, x: ox, y: oy }));
      },
      onPinch: ({ offset: [s, a], memo }) => {
        const nextScale = Math.min(3.2, Math.max(0.35, s));
        setTransform(t => clamp({ ...t, scale: nextScale, rotate: a }));
        return memo;
      },
    },
    {
      target: ref,
      drag: { from: () => [transform.x, transform.y], filterTaps: true, pointer: { touch: true } },
      pinch: { scaleBounds: { min: 0.35, max: 3.2 }, rubberband: true, from: () => [transform.scale, transform.rotate] },
      eventOptions: { passive: false },
    }
  );

  // The creature-only rendering falls back to the framed card until the
  // cutout is ready or if background removal fails.
  const showCreature = mode === 'creature' && cutoutUrl && !cutErr;
  const displayImg = showCreature ? cutoutUrl : rawImg;

  return (
    <div
      ref={ref}
      data-testid="ar-card-overlay"
      onPointerDown={() => onActivate?.()}
      className="absolute left-1/2 top-1/2 select-none touch-none z-30"
      style={{
        width: transform.width,
        height: transform.height,
        transform: `translate(-50%, -50%) translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale}) rotate(${transform.rotate}deg)`,
        transformOrigin: 'center',
        cursor: 'grab',
        willChange: 'transform',
        filter: active ? 'none' : 'brightness(0.9)',
      }}
    >
      {showCreature ? (
        // Creature-only: transparent PNG with a soft grounded drop-shadow
        // so it looks like the animal is really standing beside the user.
        <div className="relative w-full h-full">
          <img
            src={displayImg}
            alt={card.species_name || 'creature'}
            crossOrigin="anonymous"
            draggable={false}
            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
            style={{ filter: 'drop-shadow(0 12px 18px rgba(0,0,0,0.45)) drop-shadow(0 2px 3px rgba(0,0,0,0.35))' }}
          />
          {/* Ground contact shadow — flattened ellipse under the creature */}
          <div
            className="absolute left-1/2 bottom-1 -translate-x-1/2 rounded-[50%] pointer-events-none"
            style={{
              width: '55%', height: '6%',
              background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0) 75%)',
              filter: 'blur(2px)',
            }}
          />
        </div>
      ) : (
        // Framed card (default fallback + Card view mode)
        <div
          className={`w-full h-full rounded-2xl overflow-hidden ${ui.borderClass}`}
          style={{ ...ui.borderStyle, boxShadow: '0 20px 60px rgba(0,0,0,0.55), 0 0 0 3px rgba(0,0,0,0.35)' }}
        >
          {rawImg ? (
            <img
              src={rawImg}
              alt={card.species_name || 'card'}
              crossOrigin="anonymous"
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
          ) : (
            <div className="absolute inset-0 bg-neutral-800" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/0 to-black/25 pointer-events-none" />
          <div className="absolute inset-x-3 top-2 flex items-center justify-between pointer-events-none">
            <span className="text-[9px] uppercase tracking-widest font-mono text-white/90">WildQuest</span>
            <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded-full bg-black/50" style={{ color: ui.color }}>
              {ui.label}
            </span>
          </div>
          <div className="absolute inset-x-3 bottom-2.5 pointer-events-none">
            <p className="text-[11px] font-serif leading-tight text-white line-clamp-2">{card.species_name || 'Wildlife'}</p>
            {card.scientific_name && (
              <p className="text-[9px] italic text-white/70 mt-0.5 line-clamp-1">{card.scientific_name}</p>
            )}
          </div>
        </div>
      )}

      {/* Cutout progress badge */}
      {mode === 'creature' && cutting && !cutoutUrl && (
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/70 border border-white/10 pointer-events-none">
          <Loader2 className="w-3 h-3 animate-spin text-accent" />
          <span className="text-[9px] uppercase tracking-widest text-white/85">Cutting out {card.species_name || 'creature'}…</span>
        </div>
      )}
    </div>
  );
}
